/**
 * Czytelny komunikat dla odrzucenia ZAPISU wartości słownikowych w kartotece lub wierszu tabeli
 * dodatkowej.
 *
 * Serwer odrzuca część zapisu dotyczącą słowników kodem 400 z jednym z trzech ciał (te same w
 * zapisie całej kartoteki — `mapExtWriteError` (serwer: `apps/api/lib/ext-fields/map-ext-write-error.ts`) — i w zapisie pojedynczego wiersza tabeli
 * dodatkowej):
 *  - `{ error: 'INVALID_DICTIONARY_VALUE', dictionaryCode, value }` — pozycja niedozwolona w
 *    słowniku (np. po zmianie pola kartoteki, które zawęża listę słownika),
 *  - `{ error: 'EXTERNAL_PARAM_MISSING', paramName }` — wymagany parametr słownika nie ma wartości,
 *  - `{ error: 'EXTERNAL_PARAM_INVALID', paramName, reason? }` — wartość parametru złego typu.
 *
 * Wołający (strona kartoteki / wiersz tabeli dodatkowej) robi jedno wywołanie w `onError`:
 * gdy helper zwróci komunikat — pokazuje go zamiast ogólnego „nie udało się zapisać"; gdy zwróci
 * `null` — zostaje przy swoim dotychczasowym komunikacie (inny błąd, nie-axios, albo ciało bez
 * wymaganych pól, więc nie zgadujemy treści).
 */

import { isAxiosError } from 'axios';

import { extractErrorCode } from '@/lib/errors';

/** Ten sam kształt co `mapDictionaryQueryError` — `t` z `useTranslation()` pasuje bez rzutowania. */
type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

/** Pole tekstowe z ciała odpowiedzi błędu — `undefined`, gdy brak albo nie jest niepustym stringiem. */
function readBodyString(err: unknown, field: string): string | undefined {
  if (!isAxiosError(err)) return undefined;
  const data: unknown = err.response?.data;
  if (typeof data !== 'object' || data === null) return undefined;
  const value = (data as Record<string, unknown>)[field];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/**
 * Skąd pochodzi odrzucony zapis — decyduje o treści komunikatu `INVALID_DICTIONARY_VALUE`:
 *  - `'record'` (domyślnie) — zapis całej kartoteki: błąd może pochodzić z dowolnego pola
 *    słownikowego (nagłówek, pola dodatkowe) albo z wiersza tabeli dodatkowej, więc komunikat
 *    jest neutralny (nie wskazuje tabeli),
 *  - `'tableRow'` — zapis pojedynczego wiersza tabeli dodatkowej: błąd może pochodzić tylko z tego
 *    wiersza, więc komunikat odsyła do zmiany wyboru w tabeli.
 */
export type DictionarySaveErrorSource = 'record' | 'tableRow';

/**
 * Komunikat po polsku/angielsku (wg `t`) dla jednego z trzech błędów słownikowych zapisu albo
 * `null`, gdy `err` nim nie jest.
 */
export function mapDictionarySaveError(
  err: unknown,
  t: TranslateFn,
  source: DictionarySaveErrorSource = 'record',
): string | null {
  switch (extractErrorCode(err)) {
    case 'INVALID_DICTIONARY_VALUE': {
      const dictionaryCode = readBodyString(err, 'dictionaryCode');
      const value = readBodyString(err, 'value');
      if (dictionaryCode === undefined || value === undefined) return null;
      const key =
        source === 'tableRow'
          ? 'tenant.dictionaries.saveError.invalidValueTableRow'
          : 'tenant.dictionaries.saveError.invalidValue';
      return t(key, { dictionaryCode, value });
    }
    case 'EXTERNAL_PARAM_MISSING': {
      const paramName = readBodyString(err, 'paramName');
      if (paramName === undefined) return null;
      return t('tenant.dictionaries.saveError.paramMissing', { paramName });
    }
    case 'EXTERNAL_PARAM_INVALID': {
      const paramName = readBodyString(err, 'paramName');
      if (paramName === undefined) return null;
      return t('tenant.dictionaries.saveError.paramInvalid', { paramName });
    }
    default:
      return null;
  }
}
